import { Controller } from "stimulus";

// Switches a preview pane between its tabs and opens it full size over the page. The tab buttons float over
// the content, outside the Turbo frame the form's `preview` controller reloads (the panels are inside it), so
// the chosen tab is kept here and re-applied each time the frame brings in fresh panels.
export default class extends Controller {
  static targets = ["tab", "expand", "collapse"];
  static classes = ["expanded"];
  static values = { selected: String };

  connect() {
    this.apply();
  }

  select(event) {
    this.selectedValue = event.params.panel;
  }

  // Opens the pane over the page at desktop size (overlapping the editor and nav), and puts it back. The
  // page's own stacking contexts keep it under the nav, so while open it lives on <body>, with a comment
  // marking where it goes back to. Moving it reconnects the controller, so that state is kept on the
  // element. The dimmed backdrop is the element's own box-shadow.
  expand() {
    const el = this.element;
    if (el.home) return;
    el.home = document.createComment("preview-pane");
    el.before(el.home);
    document.body.append(el);
    el.onOutsideClick = (event) => {
      if (!el.contains(event.target)) this.collapse();
    };
    // Deferred so the click that opened it doesn't close it again.
    setTimeout(() => document.addEventListener("click", el.onOutsideClick));
    this.toggleExpanded(true);
  }

  // While it was on <body> the form's preview controller couldn't find the frame, so it skipped any reload;
  // the event tells the form to refresh the frame now that it is back in scope.
  collapse() {
    const el = this.element;
    if (!el.home) return;
    document.removeEventListener("click", el.onOutsideClick);
    el.home.replaceWith(el);
    el.home = null;
    this.toggleExpanded(false);
    this.dispatch("collapsed");
  }

  toggleExpanded(open) {
    this.expandedClasses.forEach((name) => this.element.classList.toggle(name, open));
    this.expandTarget.hidden = open;
    this.collapseTarget.hidden = !open;
  }

  selectedValueChanged() {
    this.apply();
  }

  // Bound to turbo:frame-load on the wrapper.
  apply() {
    this.element.querySelectorAll("[data-panel]").forEach((panel) => {
      panel.hidden = panel.dataset.panel !== this.selectedValue;
    });
    this.tabTargets.forEach((tab) => {
      tab.setAttribute("aria-selected", String(tab.dataset.tab === this.selectedValue));
    });
  }
}
